import React, { useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * ImageDropUpload
 *
 * Props:
 *   - uploadDir        : 'member' | 'research' | 'home'
 *   - currentImageLink : 현재 DB에 저장된 경로 (미리보기용)
 *   - imageBaseUrl     : 미리보기 URL 접두사
 *
 * Ref:
 *   - upload() : 선택된 파일을 서버에 업로드.
 *                파일 없으면 null 반환 (기존 이미지 유지).
 *                성공 시 imageLink(string) 반환, 실패 시 false 반환.
 */
const ImageDropUpload = forwardRef(function ImageDropUpload(
  { uploadDir = 'member', currentImageLink, imageBaseUrl },
  ref
) {
  const [isDragging, setIsDragging] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const displayUrl = previewUrl
    || (currentImageLink ? `${imageBaseUrl}${currentImageLink}` : null);

  const selectFile = useCallback((file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 선택 가능합니다.');
      return;
    }
    setError(null);
    setPendingFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  // 부모 저장 버튼에서 호출 → imageLink 반환 or null(변경없음) or false(실패)
  useImperativeHandle(ref, () => ({
    async upload() {
      if (!pendingFile) return null; // 새 파일 없음 → 기존 값 유지

      setUploading(true);
      setError(null);
      try {
        const formData = new FormData();
        formData.append('image', pendingFile);

        const response = await fetch(`${API_URL}/upload/image?dir=${uploadDir}`, {
          method: 'POST',
          body: formData,
        });
        const data = await response.json();

        if (data.success) {
          setPendingFile(null);
          return data.imageLink; // 성공: imageLink 반환
        } else {
          setError(data.error || '업로드 실패');
          return false;
        }
      } catch (err) {
        setError('서버 연결 실패');
        return false;
      } finally {
        setUploading(false);
      }
    },
  }));

  const handleDrop = (e) => { e.preventDefault(); setIsDragging(false); selectFile(e.dataTransfer.files[0]); };
  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleClick = () => inputRef.current.click();
  const handleFileChange = (e) => { selectFile(e.target.files[0]); e.target.value = ''; };

  return (
    <div>
      {displayUrl && (
        <div style={{ marginBottom: '10px' }}>
          <img src={displayUrl} alt="preview"
            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #ddd', display: 'block' }}
          />
        </div>
      )}
      <div
        onClick={handleClick} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
        style={{
          border: `2px dashed ${isDragging ? '#0066cc' : pendingFile ? '#28a745' : '#aaa'}`,
          borderRadius: '8px', padding: '30px', textAlign: 'center',
          cursor: 'pointer', backgroundColor: isDragging ? '#e8f0fe' : pendingFile ? '#f0fff4' : '#fafafa',
          transition: 'all 0.2s', userSelect: 'none',
        }}
      >
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        {uploading ? (
          <p style={{ margin: 0, color: '#555' }}>⏳ 업로드 중...</p>
        ) : pendingFile ? (
          <>
            <p style={{ margin: 0, fontSize: '15px', color: '#28a745' }}>✅ {pendingFile.name}</p>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#999' }}>저장 시 업로드됩니다. 다른 파일을 선택하려면 클릭하세요.</p>
          </>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: '15px', color: '#555' }}>이미지를 드래그하거나 클릭해서 선택</p>
            <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#999' }}>PNG, JPG, GIF, WEBP 지원</p>
          </>
        )}
      </div>
      {error && <p style={{ color: '#dc3545', marginTop: '8px', fontSize: '14px' }}>{error}</p>}
    </div>
  );
});

export default ImageDropUpload;
